import { useState, useEffect, useMemo, useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { AlertTriangle, Filter, Gamepad2, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { getGameRegistry } from '@quebon/games'
import type { GameCategory } from '@quebon/games'
import { handleApiError } from '@/api/client'
import { gameApi, type GameResponse } from '@/api/games'
import { GameLessonsPanel } from '@/components/games/GameLessonsPanel'
import {
  LcPlaygroundFixedOrderCell,
  MAX_FIXED_ORDER,
} from '@/components/games/LcPlaygroundFixedOrderCell'

const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '전체 카테고리' },
  { value: 'number', label: 'Number' },
  { value: 'shape', label: 'Shape' },
  { value: 'transition', label: 'Transition' },
  { value: 'casual', label: 'Casual' },
  { value: 'racing', label: 'Racing' },
  { value: 'defense', label: 'Defense' },
  { value: 'puzzle', label: 'Puzzle' },
  { value: 'action', label: 'Action' },
  { value: 'shooting', label: 'Shooting' },
]

const STATUS_OPTIONS = [
  { value: '', label: '전체 상태' },
  { value: 'published', label: 'Published' },
  { value: 'draft', label: 'Draft' },
  { value: 'lcFixed', label: 'LC 놀이터 고정' },
]

interface MergedGame {
  gameKey: string
  name: string
  category: GameCategory
  published: boolean
  linkedLessonCount: number
  lcPlaygroundFixedOrder: number | null
  image: string
}

export default function GameManagement() {
  const queryClient = useQueryClient()
  const [categoryFilter, setCategoryFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const registry = useMemo(() => getGameRegistry(), [])

  const { data: backendGames, isLoading } = useQuery({
    queryKey: ['admin', 'games'],
    queryFn: gameApi.getAll,
    staleTime: 30_000,
  })

  const { mutate: syncGames, isPending: isSyncing } = useMutation({
    mutationFn: gameApi.sync,
    onSuccess: (data) => {
      queryClient.setQueryData(['admin', 'games'], data)
    },
  })

  useEffect(() => {
    if (!backendGames || isSyncing) return

    const backendKeys = new Set(backendGames.map((g) => g.gameKey))
    const registryKeys = registry.map((g) => g.id)
    const missingKeys = registryKeys.filter((key) => !backendKeys.has(key))

    if (missingKeys.length > 0) {
      syncGames({ gameKeys: registryKeys })
    }
  }, [backendGames, isSyncing, registry, syncGames])

  const toggleMutation = useMutation({
    mutationFn: gameApi.togglePublish,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'games'] })
    },
  })

  // 저장 실패 시 입력칸을 서버 값으로 되돌리기 위해 셀을 다시 마운트한다
  const [fixedOrderResetNonce, setFixedOrderResetNonce] = useState(0)

  const fixedOrderMutation = useMutation({
    mutationFn: ({ gameKey, order }: { gameKey: string; order: number | null }) =>
      gameApi.setLcPlaygroundFixedOrder(gameKey, order),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'games'] })
      if (saved.lcPlaygroundFixedOrder == null) {
        toast.success('LC 놀이터 고정을 해제했습니다.')
      } else if (!saved.published) {
        toast.warning('고정했지만 비공개 게임이라 공개 전까지 학습자에게 보이지 않습니다.')
      } else {
        toast.success(`LC 놀이터 고정 순서를 ${saved.lcPlaygroundFixedOrder}(으)로 저장했습니다.`)
      }
    },
    onError: (error) => {
      toast.error(handleApiError(error))
      setFixedOrderResetNonce((n) => n + 1)
    },
  })

  const handleToggle = useCallback(
    (game: MergedGame) => {
      if (!toggleMutation.isPending) {
        if (!game.published && game.linkedLessonCount === 0 && game.lcPlaygroundFixedOrder == null) {
          toast.warning('연결된 레슨이 없는 게임을 공개합니다. LC 학습자에게는 노출되지 않습니다.')
        }
        toggleMutation.mutate(game.gameKey)
      }
    },
    [toggleMutation]
  )

  const mergedGames: MergedGame[] = useMemo(() => {
    if (!backendGames) return []

    const backendMap = new Map<string, GameResponse>()
    for (const g of backendGames) {
      backendMap.set(g.gameKey, g)
    }

    return registry
      .map((entry) => {
        const backend = backendMap.get(entry.id)
        return {
          gameKey: entry.id,
          name: entry.ko.name,
          category: entry.type,
          published: backend?.published ?? false,
          linkedLessonCount: backend?.linkedLessonCount ?? 0,
          lcPlaygroundFixedOrder: backend?.lcPlaygroundFixedOrder ?? null,
          image: entry.image,
        }
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'ko'))
  }, [registry, backendGames])

  const [panelKey, setPanelKey] = useState<string | null>(null)

  const filteredGames = useMemo(() => {
    const filtered = mergedGames.filter((game) => {
      if (categoryFilter && game.category !== categoryFilter) return false
      if (statusFilter === 'published' && !game.published) return false
      if (statusFilter === 'draft' && game.published) return false
      if (statusFilter === 'lcFixed' && game.lcPlaygroundFixedOrder == null) return false
      return true
    })
    if (statusFilter === 'lcFixed') {
      // 학습자 놀이터 표시 순서와 같게: 고정 순서, 같으면 gameKey
      return [...filtered].sort(
        (a, b) =>
          (a.lcPlaygroundFixedOrder ?? 0) - (b.lcPlaygroundFixedOrder ?? 0) ||
          a.gameKey.localeCompare(b.gameKey)
      )
    }
    return filtered
  }, [mergedGames, categoryFilter, statusFilter])

  const publishedCount = useMemo(
    () => mergedGames.filter((g) => g.published).length,
    [mergedGames]
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="size-8 animate-spin text-blue-500" />
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Gamepad2 className="size-6 text-slate-700" />
          <h1 className="text-xl font-bold text-slate-900">
            게임 관리
            <span className="ml-2 text-sm font-normal text-slate-500">
              ({publishedCount}/{mergedGames.length} published)
            </span>
          </h1>
        </div>
        {isSyncing && (
          <div className="flex items-center gap-2 text-sm text-blue-600">
            <Loader2 className="size-4 animate-spin" />
            동기화 중...
          </div>
        )}
      </div>

      <p className="mb-4 text-sm text-slate-500">
        LC 놀이터 고정: 순서(1~{MAX_FIXED_ORDER})를 입력하면 연결 레슨 완료와 상관없이 모든 LC
        센터 학습자 놀이터에 그 순서로 먼저 열립니다. 공개(Published) 게임만 노출되며, 비우면 해제됩니다.
      </p>

      <div className="flex items-center gap-3 mb-4">
        <Filter className="size-4 text-slate-400" />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
        >
          {CATEGORY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <span className="text-sm text-slate-500">
          {filteredGames.length}개 게임
        </span>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase w-16">
                썸네일
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">
                게임명
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">
                Key
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">
                카테고리
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">
                사용 레슨
              </th>
              <th className="text-center px-4 py-3 text-xs font-medium text-slate-500 uppercase">
                LC 놀이터 고정
              </th>
              <th className="text-center px-4 py-3 text-xs font-medium text-slate-500 uppercase">
                상태
              </th>
              <th className="text-center px-4 py-3 text-xs font-medium text-slate-500 uppercase w-20">
                액션
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredGames.map((game) => (
              <tr key={game.gameKey} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <img
                    src={game.image}
                    alt={game.name}
                    className="w-12 h-9 object-cover rounded"
                  />
                </td>
                <td className="px-4 py-3 text-sm font-medium text-slate-900">
                  {game.name}
                </td>
                <td className="px-4 py-3 text-sm text-slate-500 font-mono">
                  {game.gameKey}
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                    {game.category}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-slate-700">
                      {game.linkedLessonCount}개
                    </span>
                    <button
                      type="button"
                      onClick={() => setPanelKey(game.gameKey)}
                      className="text-xs font-medium text-blue-600 hover:underline"
                    >
                      연결 관리
                    </button>
                    {game.published &&
                      game.linkedLessonCount === 0 &&
                      game.lcPlaygroundFixedOrder == null && (
                      <span
                        className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700"
                        title="공개 상태이지만 연결된 레슨이 없어 LC 학습자에게 노출되지 않습니다."
                      >
                        <AlertTriangle className="size-3" />
                        미연결
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <LcPlaygroundFixedOrderCell
                    key={fixedOrderResetNonce}
                    gameName={game.name}
                    order={game.lcPlaygroundFixedOrder}
                    published={game.published}
                    disabled={fixedOrderMutation.isPending}
                    onSave={(order) =>
                      fixedOrderMutation.mutate({ gameKey: game.gameKey, order })
                    }
                    onInvalid={() =>
                      toast.error(`고정 순서는 1~${MAX_FIXED_ORDER} 사이 정수로 입력하세요. 비우면 해제됩니다.`)
                    }
                  />
                </td>
                <td className="px-4 py-3 text-center">
                  <span
                    className={clsx(
                      'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                      game.published
                        ? 'bg-green-100 text-green-700'
                        : 'bg-slate-100 text-slate-500'
                    )}
                  >
                    {game.published ? 'Published' : 'Draft'}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => handleToggle(game)}
                    disabled={toggleMutation.isPending}
                    className={clsx(
                      'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                      game.published ? 'bg-green-500' : 'bg-slate-300'
                    )}
                    aria-label={`${game.name} ${game.published ? '비공개' : '공개'}로 변경`}
                  >
                    <span
                      className={clsx(
                        'inline-block size-4 transform rounded-full bg-white transition-transform',
                        game.published ? 'translate-x-6' : 'translate-x-1'
                      )}
                    />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredGames.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            필터 조건에 맞는 게임이 없습니다.
          </div>
        )}
      </div>

      <GameLessonsPanel gameKey={panelKey} onClose={() => setPanelKey(null)} />
    </div>
  )
}
